import { reportWarning } from "@/lib/observability/report-error";

// SENTRY FIX: VIDSEEDS-2C6 — TrackedFetchError 503 GET /api/showcase/creator-results/
// SENTRY FIX: VIDSEEDS-2C4 — Failed to load creator results
// SENTRY FIX: VIDSEEDS-2C5 — Failed to load featured accounts
// SENTRY FIX: VIDSEEDS-2RR — Failed to load featured accounts
// Date: 2026-04-23 | Agent: Claude Code
// Root cause: GKE ingress returns 503 during the ~5-10s rolling-deploy window
// between pods going ready/unready. Baiduspider and normal client fetches hit
// the endpoint during that window and record the 503 in tracked-fetch before
// any user-facing recovery runs. The endpoints themselves are healthy — proved
// via direct curl (HTTP 200) immediately after the Sentry spike.
// Fix: retry transient 5xx / 408 / 429 a small number of times with exponential
// backoff before surfacing the error. If all retries fail, the caller still
// sees the original Response and decides what to do.
/**
 * Fetch helper that retries transient failures (5xx, 408, 429, network errors)
 * with exponential backoff. Non-retryable responses are returned immediately.
 *
 * Default: 3 total attempts, 400 ms → 800 ms → 1600 ms backoff (±20% jitter),
 * honoring `Retry-After` when the server sends one.
 */
export interface FetchWithTransientRetryOptions extends RequestInit {
  /** Total attempts including the first. Default 3. Clamped to [1, 6]. */
  attempts?: number;
  /** Initial backoff delay in ms. Default 400. */
  baseDelayMs?: number;
  /** Maximum backoff delay in ms. Default 4000. */
  maxDelayMs?: number;
  /** HTTP statuses to treat as transient. Default: 408, 425, 429, 500, 502, 503, 504. */
  retryOnStatuses?: readonly number[];
  /**
   * Optional hook invoked on each retry (useful for tests / structured logs).
   * Not expected to throw; any throw is swallowed.
   */
  onRetry?: (info: {
    attempt: number;
    status?: number;
    delayMs: number;
    error?: unknown;
  }) => void;
}

// Cloudflare-specific 5xx codes (520–527) surface as edge-layer problems
// that are often transient (origin timeout, connection reset, handshake
// failure). Including them here lets small GET-heavy callers recover without
// manual handling. Callers that can't afford a retry (e.g. multi-megabyte
// POSTs) should either reduce `attempts` or override `retryOnStatuses`.
//
// Exported so consumers that classify errors AFTER this helper gives up
// (e.g. homepage featured-creators / creator-results) can key off the same
// set of retryable statuses instead of hard-coding a copy. One source of
// truth avoids silent drift — if this array changes, the classification
// picks it up automatically.
export const TRANSIENT_RETRY_STATUSES: readonly number[] = [
  408, 425, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527,
];
/**
 * O(1) `.has(status)` lookup for the same set. Frozen so consumers can't
 * mutate it.
 */
export const TRANSIENT_RETRY_STATUS_SET: ReadonlySet<number> = new Set(
  TRANSIENT_RETRY_STATUSES
);
const DEFAULT_RETRY_STATUSES: readonly number[] = TRANSIENT_RETRY_STATUSES;

const DEFAULT_ATTEMPTS = 3;
const DEFAULT_BASE_DELAY_MS = 400;
const DEFAULT_MAX_DELAY_MS = 4_000;

function jitteredDelay(base: number, cap: number, attempt: number): number {
  const exp = Math.min(cap, base * Math.pow(2, attempt));
  const jitter = 1 + (Math.random() * 0.4 - 0.2);
  return Math.max(50, Math.round(exp * jitter));
}

function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const seconds = Number.parseInt(header, 10);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(seconds * 1000, 10_000);
  }
  const httpDate = Date.parse(header);
  if (!Number.isNaN(httpDate)) {
    return Math.max(0, Math.min(httpDate - Date.now(), 10_000));
  }
  return null;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason ?? new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function isAbortError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === "AbortError" || error.name === "TimeoutError")
  );
}

/**
 * Retry a fetch call on transient errors. Returns the Response from the last
 * attempt — callers still own `response.ok` / status handling.
 */
export async function fetchWithTransientRetry(
  input: RequestInfo | URL,
  init?: FetchWithTransientRetryOptions
): Promise<Response> {
  const {
    attempts: rawAttempts,
    baseDelayMs = DEFAULT_BASE_DELAY_MS,
    maxDelayMs = DEFAULT_MAX_DELAY_MS,
    retryOnStatuses = DEFAULT_RETRY_STATUSES,
    onRetry,
    ...fetchInit
  } = init ?? {};

  const totalAttempts = Math.min(
    6,
    Math.max(1, rawAttempts ?? DEFAULT_ATTEMPTS)
  );
  const statusSet = new Set(retryOnStatuses);
  let lastError: unknown;

  for (let attempt = 0; attempt < totalAttempts; attempt++) {
    try {
      const response = await fetch(input, fetchInit);
      if (response.ok || !statusSet.has(response.status)) {
        return response;
      }
      if (attempt === totalAttempts - 1) {
        return response;
      }
      const retryAfterMs = parseRetryAfter(response.headers.get("Retry-After"));
      const delay =
        retryAfterMs ?? jitteredDelay(baseDelayMs, maxDelayMs, attempt);
      try {
        onRetry?.({
          attempt: attempt + 1,
          status: response.status,
          delayMs: delay,
        });
      } catch (onRetryError) {
        // onRetry is user-provided; a throw from it must not break the retry
        // loop, but we still want visibility.
        reportWarning(onRetryError, {
          source: "lib.utils.fetch-with-transient-retry",
          feature: "http",
          action: "onRetry-throw",
        });
      }
      await sleep(delay, fetchInit.signal ?? undefined);
    } catch (error) {
      lastError = error;
      if (isAbortError(error) || attempt === totalAttempts - 1) {
        throw error;
      }
      reportWarning(error, {
        source: "lib.utils.fetch-with-transient-retry",
        feature: "http",
        action: "transient-network-retry",
        extra: { attempt: attempt + 1, totalAttempts },
      });
      const delay = jitteredDelay(baseDelayMs, maxDelayMs, attempt);
      try {
        onRetry?.({ attempt: attempt + 1, delayMs: delay, error });
      } catch (onRetryError) {
        reportWarning(onRetryError, {
          source: "lib.utils.fetch-with-transient-retry",
          feature: "http",
          action: "onRetry-throw-after-network-error",
        });
      }
      await sleep(delay, fetchInit.signal ?? undefined);
    }
  }

  throw (
    lastError ??
    new Error("fetchWithTransientRetry: exhausted without response")
  );
}
